/**
 * ================================================================
 * API SERVICE: NotificationsService
 * ================================================================
 *
 * @service          notificationsApi
 * @description      API service for managing user notifications
 * @baseUrl          /api/notifications
 *
 * @endpoints
 * - GET    /notifications/user - Fetch user notifications with pagination and filtering
 *
 * @authentication  Bearer token required for all endpoints
 * @rateLimit        100 requests per minute per user
 *
 * @caching
 * - Strategy: RTK Query automatic caching with tags
 * - Tags: ['Notifications'] for cache invalidation
 * - TTL: 2 minutes default for real-time updates
 *
 * @errorHandling
 * - Network errors: Automatic retry with exponential backoff
 * - Auth errors: Redirect to login page
 * - Validation errors: Display user-friendly messages
 *
 * @types
 * - GetNotificationsParams: Query parameters for fetching notifications
 * - GetNotificationsResponse: API response with notifications and pagination
 * - Notification: Individual notification data structure
 *
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer, createBaseQuery, handleApiSuccess } from '@/lib/api/baseQuery';
import type { GetNotificationsParams, GetNotificationsResponse } from '@/types/notifications';

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: createBaseQuery(
    import.meta.env.VITE_API_BASE_URL || 'https://web.arhamshare.com/apb/v2',
  ),
  tagTypes: ['Notifications'], // Cache invalidation tags
  endpoints: (builder) => ({
    // ================================================================
    // QUERY ENDPOINTS (READ OPERATIONS)
    // ================================================================

    /**
     * Fetch user notifications with optional filtering and pagination
     *
     * @endpoint GET /notifications/user
     * @param {GetNotificationsParams} params - Query parameters (uid, page, limit, status)
     * @returns {GetNotificationsResponse} Paginated notifications data
     * @cache Provides 'Notifications' tag for invalidation
     */
    getUserNotifications: builder.query<GetNotificationsResponse, GetNotificationsParams>({
      query: (params) => {
        // ================================================================
        // QUERY CONSTRUCTION
        // ================================================================
        //
        // Purpose: Build URL with query parameters for notifications API
        // Parameters: uid (required), page, limit, status (optional)
        // URL Format: /notifications/user?uid=VJ&page=1&limit=10&status=All
        // ================================================================

        const searchParams = new URLSearchParams();
        searchParams.append('uid', params.uid);

        if (params.page !== undefined) {
          searchParams.append('page', params.page.toString());
        }

        if (params.limit !== undefined) {
          searchParams.append('limit', params.limit.toString());
        }

        if (params.status !== undefined) {
          searchParams.append('status', params.status);
        }

        return `notifications/user?${searchParams.toString()}`;
      },
      providesTags: (_result, _error, params) => [
        'Notifications',
        { type: 'Notifications' as const, id: `${params.uid}-${params.status || 'All'}` },
      ],
      // ================================================================
      // RESPONSE TRANSFORMATION
      // ================================================================
      //
      // Purpose: Normalize API response and handle edge cases
      // Sorting: Sort notifications by created_at in descending order
      // Validation: Ensure response structure is consistent
      // ================================================================
      transformResponse: (response: GetNotificationsResponse) => {
        return {
          ...response,
          data: {
            ...response?.data,
            notifications: response?.data?.notifications?.sort(
              (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
            ),
          },
        };
      },
      transformErrorResponse: createApiErrorTransformer('NotificationsList', false),
    }),

    // ================================================================
    // MUTATION ENDPOINTS (WRITE OPERATIONS)
    // ================================================================

    /**
     * Mark notification as read
     *
     * @endpoint PUT /notifications/user/status/{id}
     * @param {string} notificationId - ID of notification to mark as read
     * @returns {void} Success confirmation
     * @invalidates 'Notifications' tag to refresh related queries
     */
    markNotificationAsRead: builder.mutation<void, string>({
      query: (notificationId) => ({
        url: `notifications/user/status/${notificationId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Notifications'],
      // ================================================================
      // OPTIMISTIC UPDATE
      // ================================================================
      //
      // Purpose: Update UI immediately while API call is in progress
      // Fallback: Revert changes if API call fails
      // UX: Provides instant feedback to user
      // ================================================================
      async onQueryStarted(notificationId, { dispatch, queryFulfilled }) {
        // Find all cached queries that might contain this notification
        const patches: any[] = [];

        try {
          await queryFulfilled;
          handleApiSuccess('NotificationMarkRead', 'Notification marked as read.', false);

          // Update all notification queries in cache
          dispatch(
            notificationsApi.util.updateQueryData(
              'getUserNotifications',
              { uid: '', page: 1, limit: 10, status: 'All' } as any,
              (draft) => {
                const notification = draft.data.notifications.find((n) => n.id === notificationId);
                if (notification) {
                  notification.status = 'READ';
                }
              },
            ),
          );
        } catch {
          // Revert optimistic updates on failure
          patches.forEach((patch) => patch.undo());
        }
      },
      transformErrorResponse: createApiErrorTransformer('NotificationMarkRead', false),
    }),

    /**
     * Mark all notifications as read
     *
     * @endpoint PUT /notifications/user/all-status
     * @param {string} uid - User identifier
     * @returns {void} Success confirmation
     * @invalidates 'Notifications' tag to refresh related queries
     */
    markAllNotificationsAsRead: builder.mutation<void, string>({
      query: (uid) => ({
        url: `notifications/user/all-status`,
        method: 'PUT',
        body: { uid },
      }),
      invalidatesTags: ['Notifications'],
      // ================================================================
      // OPTIMISTIC UPDATE FOR ALL NOTIFICATIONS
      // ================================================================
      //
      // Purpose: Mark all notifications as read instantly
      // Scope: Updates all cached notification queries
      // UX: Immediate feedback across all components
      // ================================================================
      async onQueryStarted(uid, { dispatch, queryFulfilled }) {
        const patches: any[] = [];

        try {
          await queryFulfilled;
          handleApiSuccess('NotificationMarkAllRead', 'All notifications marked as read.', false);

          // Update all notification queries in cache
          dispatch(
            notificationsApi.util.updateQueryData(
              'getUserNotifications',
              { uid, page: 1, limit: 10, status: 'All' } as any,
              (draft) => {
                draft.data.notifications.forEach((notification) => {
                  notification.status = 'READ';
                });
                // Reset unread count
                if (draft.data.pagination) {
                  draft.data.pagination.unread_count = 0;
                }
              },
            ),
          );
        } catch {
          // Revert optimistic updates on failure
          patches.forEach((patch) => patch.undo());
        }
      },
      transformErrorResponse: createApiErrorTransformer('NotificationMarkAllRead', false),
    }),
  }),
});

// ================================================================
// EXPORTED HOOKS
// ================================================================
//
// Auto-generated hooks for use in React components
// Naming convention: use[EndpointName][Query|Mutation]
// ================================================================

export const {
  useGetUserNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useLazyGetUserNotificationsQuery,
} = notificationsApi;

/**
 * ================================================================
 * END OF SERVICE: NotificationsService
 * ================================================================
 *
 * @exports          notificationsApi, hooks for React components
 * @dependencies     RTK Query, TypeScript types
 * @usedBy           Notification components and header component
 *
 * @maintenance
 * - Cache strategy: Review TTL settings for real-time requirements
 * - Error handling: Monitor error rates and adjust retry logic
 * - Performance: Track query performance and optimize slow endpoints
 *
 * ================================================================
 */
